import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import ReactDOM from "react-dom";
import styled, { ThemeProvider } from "styled-components/macro";
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import { create } from "jss";
import debounce from "lodash.debounce";
import "mapbox-gl/dist/mapbox-gl.css";

import ToggleBasemapControl from "../../../components/map/ToggleBasemapControl";
import ResetZoomControl from "../../../components/map/ResetZoomControl";
import createTheme from "../../../theme";
import WqMapPopup from "./WqMapPopup";
import WqMapLegend from "./WqMapLegend";
import {
  BASEMAP_STYLES,
  INIT_MAP_CONFIG,
  WQ_LABELS_LAYER,
  WQ_POINTS_LAYER,
  WQ_POINTS_LAYER_ID,
  WQ_SOURCE_ID,
} from "./config";
import { fmtPaValue, isPaParameter } from "./format";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});
const popupTheme = createTheme();

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

// Render just the "value unit" portion of a label. The "ND" prefix is added
// by the symbol layer's text-field expression so it can be styled larger on
// its own line above the value. Presence/absence parameters render as
// "Present" or "Not Present" instead of 1/0 and never carry an ND prefix.
const formatValueText = (stat) => {
  if (!stat || stat.last_report == null) return "";
  if (isPaParameter(stat.aqsci_parameter_ndx)) {
    return fmtPaValue(stat.last_report, { fallback: "" });
  }
  const unit = stat.unit_abbrev || "";
  // Prefer the exactly-as-typed value: numeric → string ("2.0"), shown
  // verbatim so significant trailing zeros survive on the label.
  const raw = stat.last_report_entered ?? stat.last_report;
  if (typeof raw === "string") {
    return `${raw} ${unit}`.trim();
  }
  const num = Number(raw);
  const value = Number.isFinite(num)
    ? num.toLocaleString(undefined, { maximumFractionDigits: 3 })
    : String(raw);
  return `${value} ${unit}`.trim();
};

const WqMap = ({
  summary,
  stats,
  selectedParameterNdx,
  selectedWellNdx,
  onSelectWell,
  onParameterChange,
  onShowRecords,
  onEditEvent,
  latestEditableEventByWellParam,
  registerFlyTo,
}) => {
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const previousSelectedFeatureId = useRef(null);
  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "400px",
      offset: 15,
      focusAfterOpen: false,
    })
  );
  // Single DOM node reused across popup renders so React can reconcile
  // (instead of mounting a fresh tree per click and leaking the old one)
  const popupNodeRef = useRef(null);
  if (popupNodeRef.current === null) {
    popupNodeRef.current = document.createElement("div");
  }

  // Build lookup: well_ndx -> stat row for the selected parameter
  const statsByWell = useMemo(() => {
    if (!selectedParameterNdx || !stats?.length) return new Map();
    const m = new Map();
    stats.forEach((s) => {
      if (s.aqsci_parameter_ndx === selectedParameterNdx) {
        m.set(s.well_ndx, s);
      }
    });
    return m;
  }, [stats, selectedParameterNdx]);

  const summaryByWell = useMemo(() => {
    const m = new Map();
    summary?.forEach((s) => m.set(s.well_ndx, s));
    return m;
  }, [summary]);

  // Group all stats by well_ndx (each well's full per-parameter list).
  // The popup uses this to render one tab per parameter for that well.
  const allStatsByWell = useMemo(() => {
    const m = new Map();
    stats?.forEach((s) => {
      if (!m.has(s.well_ndx)) m.set(s.well_ndx, []);
      m.get(s.well_ndx).push(s);
    });
    // Match Postgres's default case-sensitive ASCII order (used by the
    // wq_data_list_parameters view) so chip and tab orders agree.
    m.forEach((arr) => {
      arr.sort((a, b) => {
        const aKey = a.parameter_abbrev || "";
        const bKey = b.parameter_abbrev || "";
        if (aKey < bKey) return -1;
        if (aKey > bKey) return 1;
        return 0;
      });
    });
    return m;
  }, [stats]);

  // Init map once
  useEffect(() => {
    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      ...INIT_MAP_CONFIG,
    });

    m.addControl(new mapboxgl.NavigationControl(), "top-left");
    m.addControl(new mapboxgl.FullscreenControl(), "top-left");
    m.addControl(new ResetZoomControl(), "top-left");
    m.addControl(
      new mapboxgl.ScaleControl({ unit: "imperial" }),
      "bottom-left"
    );
    BASEMAP_STYLES.forEach((layer) => {
      m.addControl(
        new ToggleBasemapControl(layer.url, layer.icon),
        "bottom-right"
      );
    });

    m.on("load", () => {
      setMap(m);
      setMapLoaded(true);
    });

    // Unmount React tree if popup is closed (X button, click elsewhere, etc.)
    popUpRef.current.on("close", () => {
      if (popupNodeRef.current) {
        ReactDOM.unmountComponentAtNode(popupNodeRef.current);
      }
    });

    return () => {
      if (popupNodeRef.current) {
        ReactDOM.unmountComponentAtNode(popupNodeRef.current);
      }
      m.remove();
    };
  }, []);

  // Resize map when container resizes (sidebar toggles, etc.)
  useEffect(() => {
    if (!map) return undefined;
    const resizer = new ResizeObserver(debounce(() => map.resize(), 100));
    resizer.observe(mapContainerRef.current);
    return () => resizer.disconnect();
  }, [map]);

  // Aquifer completions toggled off via the legend. Stacks with the
  // parameter filter — a well must pass both to stay on the map. Wells
  // without the aquifer fields (pre-migration payloads) are never hidden.
  const [disabledAquifers, setDisabledAquifers] = useState(() => new Set());

  const toggleAquifer = useCallback(
    (label) => {
      const turningOff = !disabledAquifers.has(label);
      // Hiding the aquifer of the currently-selected well would leave its
      // popup floating over an empty spot — clear the selection first.
      if (turningOff && selectedWellNdx != null) {
        const sel = summaryByWell.get(selectedWellNdx);
        if (sel?.aquifer_completion === label) {
          onSelectWell(selectedWellNdx); // toggle-style handler → clears
        }
      }
      setDisabledAquifers((prev) => {
        const next = new Set(prev);
        if (next.has(label)) {
          next.delete(label);
        } else {
          next.add(label);
        }
        return next;
      });
    },
    [disabledAquifers, selectedWellNdx, summaryByWell, onSelectWell]
  );

  // Wells passing the parameter filter (before aquifer toggles). The legend
  // fallback derives from this set so a toggled-off aquifer keeps its row —
  // otherwise it could never be re-enabled.
  const paramFilteredWells = useMemo(() => {
    if (!summary?.length) return [];
    return summary
      .filter((s) => s.lat_dd != null && s.lon_dd != null)
      .filter((s) => {
        if (!selectedParameterNdx) return true;
        return Array.isArray(s.parameters)
          ? s.parameters.includes(selectedParameterNdx)
          : false;
      });
  }, [summary, selectedParameterNdx]);

  // Wells currently shown on the map (parameter filter + aquifer toggles)
  const visibleWells = useMemo(
    () =>
      paramFilteredWells.filter(
        (s) =>
          !s.aquifer_completion || !disabledAquifers.has(s.aquifer_completion)
      ),
    [paramFilteredWells, disabledAquifers]
  );

  // Build features whenever data or parameter selection changes
  const features = useMemo(() => {
    return visibleWells.map((s) => {
      const stat = selectedParameterNdx ? statsByWell.get(s.well_ndx) : null;
      const valueText = formatValueText(stat);
      return {
        type: "Feature",
        id: s.well_ndx,
        properties: {
          well_ndx: s.well_ndx,
          well_name: s.well_name,
          aquifer_completion: s.aquifer_completion,
          aquifer_color: s.aquifer_color,
          value_text: valueText,
          // PA params already render "Not Present" so no ND prefix.
          is_nd: !!(
            stat &&
            stat.is_nondetect &&
            valueText &&
            !isPaParameter(stat.aqsci_parameter_ndx)
          ),
        },
        geometry: {
          type: "Point",
          coordinates: [Number(s.lon_dd), Number(s.lat_dd)],
        },
      };
    });
  }, [visibleWells, selectedParameterNdx, statsByWell]);

  // Legend entries: only aquifers that some WQ well actually has, ordered
  // like the interactive map's legend (lookup order_by). Derived from the
  // full dataset (not the filtered set) so the legend stays stable across
  // parameter filters and toggled-off aquifers keep their row. Wells
  // without the aquifer fields (pre-migration payloads) are skipped so the
  // legend never disagrees with the fallback-colored dots.
  const legendItems = useMemo(() => {
    const m = new Map();
    summary?.forEach((s) => {
      if (s.lat_dd == null || s.lon_dd == null) return;
      if (!s.aquifer_completion || m.has(s.aquifer_completion)) return;
      m.set(s.aquifer_completion, {
        label: s.aquifer_completion,
        color: s.aquifer_color || "#737373",
        order: s.aquifer_order ?? 999,
      });
    });
    return [...m.values()].sort(
      (a, b) => a.order - b.order || a.label.localeCompare(b.label)
    );
  }, [summary]);

  // Add/update source + layers
  useEffect(() => {
    if (!mapLoaded || !map) return;

    const geojson = { type: "FeatureCollection", features };

    const src = map.getSource(WQ_SOURCE_ID);
    if (!src) {
      map.addSource(WQ_SOURCE_ID, { type: "geojson", data: geojson });
      map.addLayer(WQ_POINTS_LAYER);
      map.addLayer(WQ_LABELS_LAYER);
    } else {
      src.setData(geojson);
    }
  }, [map, mapLoaded, features]);

  // Click handler — registers once per map
  useEffect(() => {
    if (!mapLoaded || !map) return undefined;

    const handleClick = (e) => {
      if (!e.features?.length) return;
      const wellNdx = e.features[0].properties.well_ndx;
      onSelectWell(wellNdx);
    };

    const handleEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const handleLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("click", WQ_POINTS_LAYER_ID, handleClick);
    map.on("mouseenter", WQ_POINTS_LAYER_ID, handleEnter);
    map.on("mouseleave", WQ_POINTS_LAYER_ID, handleLeave);

    return () => {
      map.off("click", WQ_POINTS_LAYER_ID, handleClick);
      map.off("mouseenter", WQ_POINTS_LAYER_ID, handleEnter);
      map.off("mouseleave", WQ_POINTS_LAYER_ID, handleLeave);
    };
  }, [map, mapLoaded, onSelectWell]);

  // Pan the map (after layout) so the popup is fully visible inside the
  // map container. Mapbox's auto-anchor only picks a side; it never pans.
  // If the map is currently animating (e.g., a search-triggered flyTo),
  // wait for it to finish before measuring — otherwise our panBy fights
  // the in-flight animation and the well lands at the map edge.
  const ensurePopupVisible = useCallback(() => {
    if (!map || !popUpRef.current.isOpen()) return;
    if (map.isMoving() || map.isEasing() || map.isZooming()) {
      map.once("moveend", () => ensurePopupVisible());
      return;
    }
    const popupEl = popUpRef.current.getElement();
    if (!popupEl) return;

    const popupRect = popupEl.getBoundingClientRect();
    const mapRect = map.getContainer().getBoundingClientRect();
    const margin = 12;

    let dx = 0;
    let dy = 0;
    if (popupRect.top < mapRect.top + margin) {
      dy = popupRect.top - (mapRect.top + margin);
    } else if (popupRect.bottom > mapRect.bottom - margin) {
      dy = popupRect.bottom - (mapRect.bottom - margin);
    }
    if (popupRect.left < mapRect.left + margin) {
      dx = popupRect.left - (mapRect.left + margin);
    } else if (popupRect.right > mapRect.right - margin) {
      dx = popupRect.right - (mapRect.right - margin);
    }

    if (dx !== 0 || dy !== 0) {
      map.panBy([dx, dy], { duration: 250 });
    }
  }, [map]);

  // Update feature-state for selection + render popup
  const renderPopup = useCallback(
    (wellNdx) => {
      if (!map || wellNdx == null) return;
      const summaryRow = summaryByWell.get(wellNdx);
      if (!summaryRow) return;

      const paramStats = allStatsByWell.get(wellNdx) || [];

      // Re-render into the same node — React reconciles instead of mounting
      // a new tree (which would leak fibers on every click).
      const node = popupNodeRef.current;
      ReactDOM.render(
        <StylesProvider jss={jss}>
          <MuiThemeProvider theme={popupTheme}>
            <ThemeProvider theme={popupTheme}>
              <WqMapPopup
                summary={summaryRow}
                paramStats={paramStats}
                activeParameterNdx={selectedParameterNdx}
                onTabChange={onParameterChange}
                onShowRecords={onShowRecords}
                onEditEvent={onEditEvent}
                latestEditableEventByWellParam={latestEditableEventByWellParam}
              />
            </ThemeProvider>
          </MuiThemeProvider>
        </StylesProvider>,
        node
      );

      const coords = [Number(summaryRow.lon_dd), Number(summaryRow.lat_dd)];
      const wasOpen = popUpRef.current.isOpen();

      if (wasOpen) {
        // Already mounted on the map — let React reconciliation update the
        // content in place. Calling setDOMContent again would remove/re-append
        // the node and recreate the close button, which empties the popup
        // briefly when switching parameters.
        popUpRef.current.setLngLat(coords);
      } else {
        popUpRef.current.setLngLat(coords).setDOMContent(node).addTo(map);
      }

      // Wait for the next frame so Mapbox has positioned the popup, then pan
      // the map if it would otherwise extend past the container edge.
      requestAnimationFrame(() => {
        requestAnimationFrame(ensurePopupVisible);
      });
    },
    [
      map,
      summaryByWell,
      allStatsByWell,
      selectedParameterNdx,
      onParameterChange,
      onShowRecords,
      onEditEvent,
      latestEditableEventByWellParam,
      ensurePopupVisible,
    ]
  );

  useEffect(() => {
    if (!mapLoaded || !map) return;

    if (previousSelectedFeatureId.current != null) {
      try {
        map.setFeatureState(
          {
            source: WQ_SOURCE_ID,
            id: previousSelectedFeatureId.current,
          },
          { selected: false }
        );
      } catch (_e) {
        /* feature may no longer exist after filter */
      }
    }

    if (selectedWellNdx != null) {
      try {
        map.setFeatureState(
          { source: WQ_SOURCE_ID, id: selectedWellNdx },
          { selected: true }
        );
      } catch (_e) {
        /* ignore */
      }
      previousSelectedFeatureId.current = selectedWellNdx;
      renderPopup(selectedWellNdx);
    } else {
      popUpRef.current.remove();
      previousSelectedFeatureId.current = null;
    }
  }, [map, mapLoaded, selectedWellNdx, renderPopup]);

  // Expose flyTo to parent (used by search)
  useEffect(() => {
    if (!registerFlyTo) return;
    registerFlyTo((wellNdx) => {
      const s = summaryByWell.get(wellNdx);
      if (!s || !map) return;
      // Offset the camera so the well lands ~75% down the map (25% from
      // the bottom) instead of dead-center. That gives the popup, which
      // typically anchors above the point, room to render fully and keeps
      // the well's text label unobstructed.
      const mapHeight = map.getContainer().clientHeight;
      map.flyTo({
        center: [Number(s.lon_dd), Number(s.lat_dd)],
        zoom: 14,
        speed: 1.4,
        offset: [0, mapHeight * 0.35],
      });
    });
  }, [registerFlyTo, summaryByWell, map]);

  // The legend lives inside the map container (not beside it) so it stays
  // visible when the Mapbox fullscreen control takes the container fullscreen.
  return (
    <MapContainer ref={mapContainerRef} id="wq-map" tabIndex={-1}>
      <WqMapLegend
        items={legendItems}
        disabled={disabledAquifers}
        onToggle={toggleAquifer}
      />
    </MapContainer>
  );
};

export default WqMap;
